import React, { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, Calendar, Building2, Search, X, CheckCircle } from 'lucide-react';
import { Partner } from '../types';
import { PocketBaseDataService as DataService } from '../services/pocketbaseDataService';

interface DataDeletionManagerProps {
  partners: Partner[];
}

type QuickFilter = 'older_30_days' | 'older_60_days' | 'older_90_days' | 'custom';

export const DataDeletionManager: React.FC<DataDeletionManagerProps> = ({ partners }) => {
  const [selectedPartner, setSelectedPartner] = useState<string>('all');
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('custom');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deletionResult, setDeletionResult] = useState<{ success: boolean; count: number } | null>(null);

  // Calculate dates based on quick filter
  useEffect(() => {
    const today = new Date();
    let fromDate: Date | null = null;

    switch (quickFilter) {
      case 'older_30_days':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 30);
        setDateFrom('');
        setDateTo(fromDate.toISOString().split('T')[0]);
        break;
      case 'older_60_days':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 60);
        setDateFrom('');
        setDateTo(fromDate.toISOString().split('T')[0]);
        break;
      case 'older_90_days':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 90);
        setDateFrom('');
        setDateTo(fromDate.toISOString().split('T')[0]);
        break;
      case 'custom':
        // Keep existing dates
        break;
    }
    
    // Reset preview when filter changes
    setPreviewCount(null);
  }, [quickFilter]);

  // Preview count when filters change
  const handlePreview = async () => {
    setIsLoading(true);
    setPreviewCount(null);
    
    try {
      const { count, error } = await DataService.countPromoRequests({
        partnerId: selectedPartner !== 'all' ? selectedPartner : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });

      if (error) {
        console.error('Error counting promo requests:', error);
        alert('Failed to count records: ' + error);
      } else {
        setPreviewCount(count);
      }
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while counting records');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeletionResult(null);

    try {
      const { deletedCount, error } = await DataService.deletePromoRequestsBulk({
        partnerId: selectedPartner !== 'all' ? selectedPartner : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });

      if (error) {
        console.error('Error deleting promo requests:', error);
        alert('Failed to delete records: ' + error);
        setDeletionResult({ success: false, count: 0 });
      } else {
        setDeletionResult({ success: true, count: deletedCount });
        setPreviewCount(null);
      }
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while deleting records');
      setDeletionResult({ success: false, count: 0 });
    } finally {
      setIsDeleting(false);
      setShowConfirmModal(false);
    }
  };

  const getFilterDescription = () => {
    const parts: string[] = [];
    
    if (selectedPartner !== 'all') {
      const partner = partners.find(p => p.id === selectedPartner);
      parts.push(`Partner: ${partner?.name || selectedPartner}`);
    } else {
      parts.push('All partners');
    }

    if (dateFrom && dateTo) {
      parts.push(`From ${dateFrom} to ${dateTo}`);
    } else if (dateFrom) {
      parts.push(`From ${dateFrom}`);
    } else if (dateTo) {
      parts.push(`Before ${dateTo}`);
    }

    return parts.join(' • ');
  };

  const resetFilters = () => {
    setSelectedPartner('all');
    setQuickFilter('custom');
    setDateFrom('');
    setDateTo('');
    setPreviewCount(null);
    setDeletionResult(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Data Deletion</h2>
          <p className="text-gray-600 mt-1 text-sm">
            Permanently delete promo requests from the database. This action cannot be undone.
          </p>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Destructive Operation</h3>
            <p className="text-sm text-red-700 mt-1">
              Deleted records cannot be recovered. Make sure to export any data you need before proceeding.
              Consider backing up your database before performing bulk deletions.
            </p>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Promo Requests</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Partner Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Building2 size={16} className="inline mr-2" />
              Partner
            </label>
            <select
              value={selectedPartner}
              onChange={(e) => {
                setSelectedPartner(e.target.value);
                setPreviewCount(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="all">All Partners</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.id}>{partner.name}</option>
              ))}
            </select>
          </div>

          {/* Quick Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar size={16} className="inline mr-2" />
              Quick Filters
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setQuickFilter('older_30_days')}
                className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                  quickFilter === 'older_30_days'
                    ? 'bg-red-100 border-red-300 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                Older than 30 days
              </button>
              <button
                onClick={() => setQuickFilter('older_60_days')}
                className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                  quickFilter === 'older_60_days'
                    ? 'bg-red-100 border-red-300 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                Older than 60 days
              </button>
              <button
                onClick={() => setQuickFilter('older_90_days')}
                className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                  quickFilter === 'older_90_days'
                    ? 'bg-red-100 border-red-300 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                Older than 90 days
              </button>
              <button
                onClick={() => setQuickFilter('custom')}
                className={`px-3 py-1.5 text-sm rounded-lg border transition ${
                  quickFilter === 'custom'
                    ? 'bg-red-100 border-red-300 text-red-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                Custom Range
              </button>
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Date (optional)
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setQuickFilter('custom');
                setPreviewCount(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Date (optional)
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setQuickFilter('custom');
                setPreviewCount(null);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-6">
          <button
            onClick={resetFilters}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition flex items-center"
          >
            <X size={16} className="mr-2" />
            Reset Filters
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={handlePreview}
              disabled={isLoading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center disabled:opacity-50"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
              ) : (
                <Search size={16} className="mr-2" />
              )}
              Preview Count
            </button>

            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={previewCount === null || previewCount === 0 || isDeleting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={16} className="mr-2" />
              Delete Records
            </button>
          </div>
        </div>
      </div>

      {/* Preview Result */}
      {previewCount !== null && (
        <div className={`rounded-xl shadow-sm border p-6 ${
          previewCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {previewCount > 0 ? (
                  <>
                    <span className="text-amber-600">{previewCount.toLocaleString()}</span> promo request{previewCount !== 1 ? 's' : ''} found
                  </>
                ) : (
                  'No matching records found'
                )}
              </h3>
              <p className="text-sm text-gray-600 mt-1">{getFilterDescription()}</p>
            </div>
            {previewCount > 0 && (
              <div className="text-right">
                <p className="text-sm text-amber-700 font-medium">Ready to delete</p>
                <p className="text-xs text-gray-500">Click "Delete Records" to proceed</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Deletion Result */}
      {deletionResult && (
        <div className={`rounded-xl shadow-sm border p-6 ${
          deletionResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center">
            {deletionResult.success ? (
              <>
                <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-green-800">
                    Successfully deleted {deletionResult.count.toLocaleString()} record{deletionResult.count !== 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-green-700 mt-1">The promo requests have been permanently removed from the database.</p>
                </div>
              </>
            ) : (
              <>
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                <div>
                  <h3 className="text-lg font-semibold text-red-800">Deletion failed</h3>
                  <p className="text-sm text-red-700 mt-1">An error occurred while deleting records. Please try again.</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
                <p className="text-sm text-gray-500">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                You are about to permanently delete <strong>{previewCount?.toLocaleString()}</strong> promo request{previewCount !== 1 ? 's' : ''}.
              </p>
              <p className="text-xs text-red-700 mt-2">{getFilterDescription()}</p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                id="confirm-delete-input"
                placeholder="DELETE"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const input = document.getElementById('confirm-delete-input') as HTMLInputElement;
                  if (input.value !== 'DELETE') {
                    alert('Please type DELETE to confirm');
                    return;
                  }
                  handleDelete();
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 flex items-center justify-center disabled:opacity-50"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} className="mr-2" />
                    Delete Permanently
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataDeletionManager;
